// Catalog search — searches the REAL Apple Music catalog (via the provider), with debounce,
// loading/empty/error states, and per-row Play / Lyrics / Open actions. Plays through the
// shell-level persistent player. Provider-neutral (no MusicKit here).
import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, Play, Quote, ExternalLink, Music2, Plus } from 'lucide-react';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';
import { openProviderUrl } from '@/lib/providerLinks';
import { appleMusicProvider } from './lib/appleMusicProvider';
import { usePlayer } from './player/usePlayer';
import { MusicDetail } from './MusicDetail';
import { AddToPlaylistButton } from './AddToPlaylistButton';
import type { MusicItem, MusicSearchResults } from './lib/types';

const provider = appleMusicProvider();
const fmt = (ms?: number) => (ms ? `${Math.floor(ms / 60000)}:${String(Math.round((ms % 60000) / 1000)).padStart(2, '0')}` : '');

export const MusicSearch: React.FC<{ onAddToChallenge?: (item: MusicItem) => void; seed?: string }> = ({ onAddToChallenge, seed }) => {
  const { playItem, openPanel } = usePlayer();
  const [term, setTerm] = useState(seed ?? '');
  // Seed from a mood shortcut on Home; re-seed whenever a new mood is picked.
  useEffect(() => { if (seed) setTerm(seed); }, [seed]);
  const [results, setResults] = useState<MusicSearchResults | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [detail, setDetail] = useState<MusicItem | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(timer.current);
    const q = term.trim();
    if (!q) { setResults(null); setState('idle'); return; }
    setState('loading');
    timer.current = window.setTimeout(async () => {
      try {
        const r = await provider.searchCatalog(q, ['songs', 'artists', 'albums', 'playlists'], { limit: 12 });
        setResults(r); setState('idle');
      } catch { setState('error'); }
    }, 350);
    return () => window.clearTimeout(timer.current);
  }, [term]);

  if (detail) return <MusicDetail item={detail} onBack={() => setDetail(null)} onAddToChallenge={onAddToChallenge} />;

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search songs, artists, albums, or playlists"
          aria-label="Search Apple Music" className="pl-9" />
        {state === 'loading' && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>

      {state === 'error' && (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          Couldn’t reach Apple Music. <button className="font-medium text-primary-500 hover:underline" onClick={() => setTerm((t) => t + ' ')}>Retry</button>
        </div>
      )}

      {results && (
        <div className="space-y-4">
          {results.songs.length === 0 && results.albums.length === 0 && results.artists.length === 0 && (
            <p className="text-sm text-muted-foreground">No results for “{term}”.</p>
          )}
          {results.songs.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Songs</h3>
              <ul className="space-y-0.5">
                {results.songs.map((s) => (
                  <li key={s.id}>
                    <SongRow item={s} onPlay={() => playItem(s)} onLyrics={() => { void playItem(s).then(() => openPanel('lyrics')); }} onAdd={onAddToChallenge && (() => onAddToChallenge(s))} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {results.albums.length > 0 && <CardRow title="Albums" items={results.albums} onPlay={setDetail} />}
          {results.playlists.length > 0 && <CardRow title="Playlists" items={results.playlists} onPlay={setDetail} />}
          {results.artists.length > 0 && <CardRow title="Artists" items={results.artists} round onPlay={setDetail} />}
        </div>
      )}
    </div>
  );
};

const SongRow: React.FC<{ item: MusicItem; onPlay: () => void; onLyrics: () => void; onAdd?: (() => void) | false }> = ({ item, onPlay, onLyrics, onAdd }) => {
  const art = provider.artworkUrl(item.artwork, 44);
  return (
    <div className="group flex items-center gap-3 rounded-lg p-2 hover:bg-muted">
      <button type="button" aria-label={`Play ${item.title}`} onClick={onPlay} className="relative shrink-0">
        {art ? <img src={art} alt="" className="h-11 w-11 rounded object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded bg-muted"><Music2 className="h-4 w-4 text-muted-foreground" /></div>}
        <span className="absolute inset-0 flex items-center justify-center rounded bg-black/40 opacity-0 transition group-hover:opacity-100"><Play className="h-4 w-4 text-white" /></span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground" dir="auto">{item.title}</span>
          {item.isExplicit && <span title="Explicit" className="rounded bg-muted px-1 text-[9px] font-bold text-muted-foreground">E</span>}
        </div>
        <div className="truncate text-xs text-muted-foreground" dir="auto">{item.artist}{item.album ? ` — ${item.album}` : ''}</div>
      </div>
      <span className="hidden text-xs tabular-nums text-muted-foreground sm:block">{fmt(item.durationMs)}</span>
      <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
        <RowBtn label="Lyrics" onClick={onLyrics}><Quote className="h-4 w-4" /></RowBtn>
        <AddToPlaylistButton item={item} />
        {onAdd && <RowBtn label="Add to challenge" onClick={onAdd}><Plus className="h-4 w-4" /></RowBtn>}
        {item.canonicalUrl && <RowBtn label="Open in Apple Music" onClick={() => openProviderUrl(item.canonicalUrl!)}><ExternalLink className="h-4 w-4" /></RowBtn>}
      </div>
    </div>
  );
};

const CardRow: React.FC<{ title: string; items: MusicItem[]; round?: boolean; onPlay: (i: MusicItem) => void }> = ({ title, items, round, onPlay }) => (
  <section>
    <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
    <div className="flex gap-3 overflow-x-auto pb-1">
      {items.map((i) => {
        const art = provider.artworkUrl(i.artwork, 160);
        return (
          <button key={i.id} type="button" onClick={() => onPlay(i)} className="w-32 shrink-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
            {art ? <img src={art} alt="" className={`h-32 w-32 object-cover ${round ? 'rounded-full' : 'rounded-lg'}`} />
              : <div className={`flex h-32 w-32 items-center justify-center bg-muted ${round ? 'rounded-full' : 'rounded-lg'}`}><Music2 className="h-6 w-6 text-muted-foreground" /></div>}
            <div className="mt-1.5 truncate text-sm font-medium text-foreground" dir="auto">{i.title}</div>
            <div className="truncate text-xs text-muted-foreground" dir="auto">{i.subtitle}</div>
          </button>
        );
      })}
    </div>
  </section>
);

const RowBtn: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
  <button type="button" aria-label={label} title={label} onClick={onClick}
    className="rounded-md p-1.5 text-muted-foreground hover:bg-card hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
    {children}
  </button>
);
